import {
    Dispatch,
    RefObject,
    SetStateAction,
    useCallback,
    useState,
} from 'react';

import { Platform } from 'react-native';

import { useDispatch } from 'react-redux';

import { setPreviouslyWatchedChannel } from 'store/epg/actions.ts';
import { RBChannel } from 'store/railsBuilder/reducer.types.ts';
import { endSentryTransaction } from 'store/sentry/actions.ts';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants.ts';

import shouldConsiderCatchupAsLive from 'services/data-manager/parsing/utils/shouldConsiderCatchupAsLive.ts';
import { VideoItem } from 'services/data-manager/types.ts';

import isNumber from 'utils/number.ts';

import { PlayerRef } from 'components/Player/types.ts';

type Params = {
    playerRef: RefObject<PlayerRef | null>;
    videoItem: VideoItem | undefined;
    channel: RBChannel | undefined;
    playerPaused: boolean;
    profileId: string | undefined;
    isCatchup: boolean;
    isStartover: boolean;
    setVideoDuration: Dispatch<SetStateAction<number>>;
    setPlayerProgress: Dispatch<SetStateAction<number>>;
};

function usePlayerReady({
    playerRef,
    videoItem,
    channel,
    playerPaused,
    profileId,
    isCatchup,
    isStartover,
    setVideoDuration,
    setPlayerProgress,
}: Params) {
    const dispatch = useDispatch();

    const [readyToPlay, setReadyToPlay] = useState(false);

    const onPlayerReady = useCallback(
        async ({ duration }: { duration: number | null | undefined }) => {
            if (isCatchup && shouldConsiderCatchupAsLive(videoItem?.endTime)) {
                setVideoDuration(videoItem?.duration || 0);
            } else if (isNumber(duration) && !isStartover) {
                setVideoDuration(duration);
            } else if (videoItem?.duration != null) {
                setVideoDuration(videoItem.duration);
            } else if (videoItem?.startTime && videoItem?.endTime) {
                setVideoDuration(videoItem.endTime - videoItem.startTime);
            }

            // Update current progress value just after switching from LIVE to SO
            // to adjust progress bar position with guardTime.
            // NOT NEEDED FOR ANDROID.
            // Android recalculates progres automatically just after onReady.
            if (
                isStartover &&
                isNumber(videoItem?.guardTime) &&
                Platform.OS !== 'android'
            ) {
                setPlayerProgress(
                    (prevProgress) => prevProgress + videoItem.guardTime,
                );
            }

            if (channel && profileId) {
                dispatch(
                    setPreviouslyWatchedChannel({
                        channelId: channel.editorial.id,
                        profileId,
                    }),
                );
            }

            dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.ChannelTune));
            dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.RestartPlayback));
            dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.VODPlaybackTime));
            dispatch(
                endSentryTransaction(SENTRY_TRANSACTIONS.CatchupPlaybackTime),
            );
            dispatch(endSentryTransaction(SENTRY_TRANSACTIONS.JumpToLive));

            setReadyToPlay(true);

            if (!playerPaused) {
                playerRef.current?.play();
            } else {
                playerRef.current?.pause();
            }
        },
        [
            isCatchup,
            videoItem?.endTime,
            videoItem?.duration,
            videoItem?.startTime,
            videoItem?.guardTime,
            isStartover,
            channel,
            profileId,
            dispatch,
            playerPaused,
            setVideoDuration,
            setPlayerProgress,
            playerRef,
        ],
    );

    return { readyToPlay, setReadyToPlay, onPlayerReady };
}

export default usePlayerReady;
